import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { geofenceService } from '../services/api';
import GeofenceForm from '../components/geofence/GeofenceForm';
import LoadingSpinner from '../components/common/LoadingSpinner';

const EditGeofence = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [geofence, setGeofence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Fetch geofence data
  useEffect(() => {
    const fetchGeofence = async () => {
      try {
        setLoading(true);
        
        // In a real app, we would fetch from API
        // const response = await geofenceService.getGeofence(id);
        
        // For now, use dummy data
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Dummy geofence data based on ID
        let dummyGeofence;
        
        if (id === 'g1') {
          dummyGeofence = {
            id: 'g1',
            name: 'Downtown Zone',
            description: 'Main commercial area coverage',
            type: 'circle',
            center: { lat: 40.7128, lng: -74.006 },
            radius: 1500, // in meters
            alertType: 'both',
            isActive: true,
            createdAt: '2023-06-15T10:30:00Z',
            createdBy: {
              id: 'u1',
              name: 'Admin User'
            }
          };
        } else if (id === 'g2') {
          dummyGeofence = {
            id: 'g2',
            name: 'Airport Perimeter',
            description: 'Airport restricted access zone',
            type: 'polygon',
            vertices: [
              { lat: 40.6413, lng: -73.7781 },
              { lat: 40.6631, lng: -73.7675 },
              { lat: 40.6542, lng: -73.8101 },
              { lat: 40.6381, lng: -73.7948 }
            ],
            alertType: 'entry',
            isActive: true,
            createdAt: '2023-06-20T14:15:00Z',
            createdBy: {
              id: 'u1',
              name: 'Admin User'
            }
          };
        } else if (id === 'g3') {
          dummyGeofence = {
            id: 'g3',
            name: 'Warehouse District',
            description: 'Main logistics hub',
            type: 'circle',
            center: { lat: 40.7234, lng: -74.0123 },
            radius: 800, // in meters
            alertType: 'both',
            isActive: true,
            createdAt: '2023-07-05T09:45:00Z',
            createdBy: {
              id: 'u1',
              name: 'Admin User'
            }
          };
        } else {
          // If ID doesn't match any dummy data, simulate not found
          throw new Error('Geofence not found');
        }

        setGeofence(dummyGeofence);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to load geofence');
        setLoading(false);
        console.error('Error fetching geofence:', err);
      }
    };

    fetchGeofence();
  }, [id]);
  
  // Handle form submission
  const handleSubmit = async (geofenceData) => {
    try {
      setIsSubmitting(true);
      
      // In a real app, we would call API
      // const response = await geofenceService.updateGeofence(id, geofenceData);
      
      // For demo, just simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Geofence updated successfully');
      navigate(`/geofences/${id}`);
    } catch (err) {
      toast.error('Failed to update geofence');
      console.error('Error updating geofence:', err);
      setIsSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-ui-error">{error}</p>
        <Link to="/geofences" className="mt-4 btn btn-primary">
          Back to Geofences
        </Link>
      </div>
    );
  }

  if (!geofence) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">Geofence not found</p>
        <Link to="/geofences" className="mt-4 btn btn-primary">
          Back to Geofences
        </Link>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center">
        <Link to={`/geofences/${id}`} className="mr-4">
          <FiArrowLeft className="text-gray-500 hover:text-primary" size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Geofence</h1>
          <p className="text-gray-600">Update geofence "{geofence.name}"</p>
        </div>
      </div>
      
      {/* Geofence form */}
      <GeofenceForm 
        geofence={geofence} 
        onSubmit={handleSubmit} 
        isSubmitting={isSubmitting} 
      />
    </div>
  );
};

export default EditGeofence;